// src/pages/Reportes.js
import React, { useState, useEffect } from 'react';
import { FileText, Download, Calendar, TrendingUp, Filter, Loader, FileSpreadsheet, FileDown, CheckCircle, Clock, Activity } from 'lucide-react';
import { emergenciasAPI, departamentosAPI, municipiosAPI, eventosEmergenciaAPI, maquinariaAPI } from '../services/apiService';
import ModalRegistrarEjecucion from '../components/emergencias/ModalRegistrarEjecucion';
import ModalBoletines from '../components/emergencias/ModalBoletines';
import * as XLSX from 'xlsx';

// Función para formatear fechas
const formatearFecha = (fechaString) => {
  if (!fechaString) return 'N/A';
  
  try {
    if (fechaString.includes('-') && fechaString.split('-')[0].length <= 2) {
      const [dia, mes, año] = fechaString.split('-');
      const fecha = new Date(año, mes - 1, dia);
      return fecha.toLocaleDateString('es-HN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    }
    
    const fecha = new Date(fechaString);
    if (!isNaN(fecha.getTime())) {
      return fecha.toLocaleDateString('es-HN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    }
    
    return fechaString;
  } catch (error) {
    return fechaString;
  }
};

// Función para parsear fechas en formato DD-MM-YYYY a Date
const parsearFecha = (fechaString) => {
  if (!fechaString) return null;
  
  try {
    // Si viene en formato DD-MM-YYYY
    if (fechaString.includes('-') && fechaString.split('-')[0].length <= 2) {
      const [dia, mes, año] = fechaString.split('-');
      return new Date(año, mes - 1, dia);
    }
    
    // Si viene en otro formato
    const fecha = new Date(fechaString);
    return isNaN(fecha.getTime()) ? null : fecha;
  } catch (error) {
    return null;
  }
};

const Reportes = () => {
  const [activeReport, setActiveReport] = useState('general');
  const [emergencias, setEmergencias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [maquinaria, setMaquinaria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEjecucion, setModalEjecucion] = useState(false);
  const [modalBoletines, setModalBoletines] = useState(false);
  const [emergenciaSeleccionada, setEmergenciaSeleccionada] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    department: '',
    municipality: '',
    status: '',
    zone: '',
    evento: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emergenciasRes, deptRes, munRes, eventosRes, maqRes] = await Promise.all([
        emergenciasAPI.getAll(),
        departamentosAPI.getAll(),
        municipiosAPI.getAll(),
        eventosEmergenciaAPI.getAll(),
        maquinariaAPI.getTipos()
      ]);

      // ✅ CORRECCIÓN: Asegurar que siempre sean arrays
      let emergenciasData = emergenciasRes?.data || emergenciasRes || [];
      let deptData = deptRes?.data || deptRes || [];
      let munData = munRes?.data || munRes || [];
      let eventosData = eventosRes?.data || eventosRes || [];
      let maqData = maqRes?.data || maqRes || [];
      
      // ✅ CORRECCIÓN: Validar que sean arrays
      if (!Array.isArray(emergenciasData)) {
        console.warn('emergenciasData no es un array:', emergenciasData);
        emergenciasData = [];
      }
      if (!Array.isArray(deptData)) {
        console.warn('deptData no es un array:', deptData);
        deptData = [];
      }
      if (!Array.isArray(munData)) {
        console.warn('munData no es un array:', munData);
        munData = [];
      }
      if (!Array.isArray(eventosData)) {
        console.warn('eventosData no es un array:', eventosData);
        eventosData = [];
      }
      if (!Array.isArray(maqData)) {
        console.warn('maqData no es un array:', maqData);
        maqData = [];
      }

      console.log('=== DATOS CARGADOS ===');
      console.log('Emergencias:', emergenciasData.length);
      console.log('Departamentos:', deptData.length);
      console.log('Municipios:', munData.length);
      console.log('Eventos:', eventosData.length);
      console.log('Maquinaria (Tipos):', maqData.length);
      console.log('Muestra de maquinaria:', maqData.slice(0, 3));

      setEmergencias(emergenciasData);
      setDepartamentos(deptData);
      setMunicipios(munData);
      setEventos(eventosData);
      setMaquinaria(maqData);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (emergencia) => {
    if (emergencia.Estado === 'Por Atender') {
      try {
        await emergenciasAPI.updateBatch([{
          Id: emergencia.Id,
          Estado: 'En Atención'
        }]);
        loadData();
      } catch (error) {
        console.error('Error actualizando estado:', error);
        alert('Error al actualizar el estado');
      }
    } else if (emergencia.Estado === 'En Atención') {
      setEmergenciaSeleccionada(emergencia);
      setModalEjecucion(true);
    }
  };

  const handleSuccess = () => {
    loadData();
  };

  // ✅ CORRECCIÓN: Validar que data sea un array antes de filtrar
  const applyFilters = (data) => {
    // Asegurar que data sea un array
    if (!Array.isArray(data)) {
      console.warn('applyFilters recibió datos que no son array:', data);
      return [];
    }
    
    return data.filter(item => {
      // Parsear la fecha del item correctamente
      const fechaItem = parsearFecha(item.Fecha);
      const fechaDesde = filters.dateFrom ? new Date(filters.dateFrom) : null;
      const fechaHasta = filters.dateTo ? new Date(filters.dateTo) : null;
      
      const matchesDate = (!fechaDesde || !fechaItem || fechaItem >= fechaDesde) &&
                         (!fechaHasta || !fechaItem || fechaItem <= fechaHasta);
      const matchesDept = !filters.department || item.MunicipioFK?.startsWith(filters.department);
      const matchesMun = !filters.municipality || item.MunicipioFK === filters.municipality;
      const matchesStatus = !filters.status || item.Estado === filters.status;
      const matchesZone = !filters.zone || item.ZonaCafetalera === filters.zone;
      const matchesEvento = !filters.evento || item.EventoEmergenciaFK === parseInt(filters.evento);
      
      return matchesDate && matchesDept && matchesMun && matchesStatus && matchesZone && matchesEvento;
    });
  };

  const filteredData = applyFilters(emergencias);

  // Función para obtener TIPO de maquinaria
  const obtenerTipoMaquinaria = (codigoMaquina) => {
    if (!codigoMaquina) return 'No especificado';
    
    // Limpiar el código de la maquinaria (quitar espacios y saltos de línea)
    const codigoLimpio = String(codigoMaquina).trim().replace(/\r\n/g, '');
    
    const maq = maquinaria.find(m => {
      const idLimpio = String(m.Id).trim().replace(/\r\n/g, '');
      return idLimpio === codigoLimpio;
    });
    
    if (maq) {
      // Limpiar el tipo también (quitar saltos de línea)
      return String(maq.Tipo).trim().replace(/\r\n/g, '');
    }
    
    return codigoLimpio;
  };

  // Exportar a Excel con TODOS los datos completos (SIN ID)
  const exportarAExcel = () => {
    console.log('=== INICIANDO EXPORTACIÓN ===');
    console.log('Total emergencias a exportar:', filteredData.length);
    console.log('Total maquinaria disponible:', maquinaria.length);
    console.log('Muestra de maquinaria:', maquinaria.slice(0, 3));
    
    const datosExcel = filteredData.map(emergencia => {
      // Maquinaria Prevista - usando Tipo
      const maquinariaPrevista = emergencia.MaquinasPrevistas?.map(maq => {
        console.log('Buscando maquinaria prevista:', maq.TipoMaquinaFK);
        const tipo = obtenerTipoMaquinaria(maq.TipoMaquinaFK);
        console.log('Tipo encontrado:', tipo);
        return tipo;
      }).join(', ') || 'Sin maquinaria prevista';

      // Maquinaria Utilizada con detalles completos - usando Tipo
      const maquinariaUtilizada = emergencia.MáquinasUtilizadas?.map(maq => 
        `${obtenerTipoMaquinaria(maq.TipoMaquinaFK)} - Cant: ${maq.Cantidad}, Horas: ${maq.TotalHorasTrabajadas}, Precio/Hora: L${maq.PrecioPorHora?.toLocaleString()}, Total: L${maq.Total?.toLocaleString()}`
      ).join(' | ') || 'Sin maquinaria utilizada';

      return {
        'Fecha': emergencia.Fecha,
        'Departamento': emergencia.NombreMunicipio?.split(',')[1]?.trim() || '',
        'Municipio': emergencia.NombreMunicipio?.split(',')[0]?.trim() || emergencia.NombreMunicipio,
        'Zona Cafetalera': emergencia.ZonaCafetalera || '',
        'Nombre del Tramo': emergencia.NombreTramo || '',
        'Longitud del Tramo (km)': emergencia.LongitudTramo || '',
        'Tipo de Emergencia': emergencia.NombreTipoEmergencia || '',
        'Tipo de Red': emergencia.NombreTipoRed || '',
        'Estado': emergencia.Estado || '',
        'Ejecutor': emergencia.Ejecutor || '',
        'Inversión Proyectada (L)': emergencia.InversionProyectada || 0,
        'Inversión Total (L)': emergencia.InversionTotal || 0,
        'Latitud': emergencia.Latitud || '',
        'Longitud': emergencia.Longitud || '',
        'Observaciones': emergencia.Observaciones || '',
        'Evento/Fenómeno': emergencia.NombreFenomeno || '',
        'Maquinaria Prevista': maquinariaPrevista,
        'Maquinaria Utilizada': maquinariaUtilizada,
        'Creado Por': emergencia.NombreCreador || '',
        'Fecha Creación': emergencia.FechaCreacion || '',
        'Actualizado Por': emergencia.NombreActualizador || '',
        'Fecha Actualización': emergencia.FechaActualizacion || '',
      };
    });

    // Crear workbook
    const ws = XLSX.utils.json_to_sheet(datosExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte de Emergencias');

    // Ajustar ancho de columnas (sin columna ID)
    const colWidths = [
      { wch: 12 },  // Fecha
      { wch: 15 },  // Departamento
      { wch: 25 },  // Municipio
      { wch: 20 },  // Zona Cafetalera
      { wch: 25 },  // Nombre del Tramo
      { wch: 12 },  // Longitud
      { wch: 25 },  // Tipo de Emergencia
      { wch: 15 },  // Tipo de Red
      { wch: 15 },  // Estado
      { wch: 20 },  // Ejecutor
      { wch: 18 },  // Inversión Proyectada
      { wch: 18 },  // Inversión Total
      { wch: 12 },  // Latitud
      { wch: 12 },  // Longitud
      { wch: 30 },  // Observaciones
      { wch: 20 },  // Evento/Fenómeno
      { wch: 40 },  // Maquinaria Prevista
      { wch: 80 },  // Maquinaria Utilizada
      { wch: 30 },  // Creado Por
      { wch: 20 },  // Fecha Creación
      { wch: 30 },  // Actualizado Por
      { wch: 20 },  // Fecha Actualización
    ];
    ws['!cols'] = colWidths;

    // Descargar archivo
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Reporte_Emergencias_${fecha}.xlsx`);
  };

  // Calcular estadísticas generales
  const stats = {
    total: filteredData.length,
    atendidas: filteredData.filter(e => e.Estado === 'Atendido').length,
    enAtencion: filteredData.filter(e => e.Estado === 'En Atención').length,
    porAtender: filteredData.filter(e => e.Estado === 'Por Atender').length,
    inversionTotal: filteredData.reduce((sum, e) => sum + (parseFloat(e.InversionTotal) || 0), 0),
    inversionProyectada: filteredData.reduce((sum, e) => sum + (parseFloat(e.InversionProyectada) || 0), 0)
  };

  // Agrupar por departamento
  const byDepartment = {};
  filteredData.forEach(e => {
    const deptCode = e.MunicipioFK?.substring(0, 2);
    const dept = departamentos.find(d => d.CodigoDepartamento === deptCode);
    const deptName = dept?.NombreDepartamento || 'Desconocido';
    
    if (!byDepartment[deptName]) {
      byDepartment[deptName] = {
        name: deptName,
        total: 0,
        atendidas: 0,
        enAtencion: 0,
        porAtender: 0,
        inversion: 0
      };
    }
    
    byDepartment[deptName].total++;
    if (e.Estado === 'Atendido') byDepartment[deptName].atendidas++;
    if (e.Estado === 'En Atención') byDepartment[deptName].enAtencion++;
    if (e.Estado === 'Por Atender') byDepartment[deptName].porAtender++;
    byDepartment[deptName].inversion += parseFloat(e.InversionTotal) || 0;
  });

  const departmentStats = Object.values(byDepartment).sort((a, b) => b.total - a.total);

  // Agrupar por tipo de emergencia
  const byType = {};
  filteredData.forEach(e => {
    const type = e.NombreTipoEmergencia || 'No especificado';
    if (!byType[type]) {
      byType[type] = { name: type, count: 0, inversion: 0 };
    }
    byType[type].count++;
    byType[type].inversion += parseFloat(e.InversionTotal) || 0;
  });

  const typeStats = Object.values(byType).sort((a, b) => b.count - a.count);

  // Agrupar por Departamento con su Regional para el reporte "Por Regional"
  const byRegional = {};
  filteredData.forEach(e => {
    const deptCode = e.MunicipioFK?.substring(0, 2);
    const dept = departamentos.find(d => d.CodigoDepartamento === deptCode);
    const deptName = dept?.NombreDepartamento || 'Desconocido';
    
    // Determinar la regional: 1 = Zona Centro, 2 = Zona Norte
    let regionalName = 'Sin Regional';
    if (dept?.RegionalFK === 1) {
      regionalName = 'Zona Centro';
    } else if (dept?.RegionalFK === 2) {
      regionalName = 'Zona Norte';
    }
    
    // Agrupar por regional
    if (!byRegional[regionalName]) {
      byRegional[regionalName] = {
        name: regionalName,
        departamentos: {}
      };
    }
    
    // Dentro de cada regional, agrupar por departamento
    if (!byRegional[regionalName].departamentos[deptName]) {
      byRegional[regionalName].departamentos[deptName] = {
        name: deptName,
        count: 0,
        inversion: 0
      };
    }
    
    byRegional[regionalName].departamentos[deptName].count++;
    byRegional[regionalName].departamentos[deptName].inversion += parseFloat(e.InversionTotal) || 0;
  });

  // Convertir a array y ordenar
  const regionalStats = Object.entries(byRegional).map(([regionalName, data]) => ({
    name: regionalName,
    departamentos: Object.values(data.departamentos).sort((a, b) => b.count - a.count),
    totalCount: Object.values(data.departamentos).reduce((sum, d) => sum + d.count, 0),
    totalInversion: Object.values(data.departamentos).reduce((sum, d) => sum + d.inversion, 0)
  })).sort((a, b) => b.totalCount - a.totalCount);

  // Obtener zonas únicas para el filtro
  const uniqueZones = [...new Set(emergencias.map(e => e.ZonaCafetalera).filter(Boolean))].sort();

  // Componente StatCard
  const StatCard = ({ title, value, subtitle }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  );

  // Componente EstadoClickeable
  const EstadoClickeable = ({ emergencia }) => (
    <button
      onClick={() => handleCambiarEstado(emergencia)}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        emergencia.Estado === 'Atendido' 
          ? 'bg-green-100 text-green-800 cursor-default' 
          : emergencia.Estado === 'En Atención'
          ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
          : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
      }`}
      disabled={emergencia.Estado === 'Atendido'}
    >
      {emergencia.Estado === 'Atendido' && <CheckCircle className="w-3 h-3 mr-1" />}
      {emergencia.Estado === 'En Atención' && <Activity className="w-3 h-3 mr-1 animate-pulse" />}
      {emergencia.Estado === 'Por Atender' && <Clock className="w-3 h-3 mr-1" />}
      {emergencia.Estado}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[#c8a88c] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="w-full px-2 sm:px-4 lg:px-6">
        {/* Encabezado con botones */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#c8a88c]" />
              Reportes de Emergencias
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Análisis y estadísticas de emergencias en la red vial cafetalera.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportarAExcel}
              disabled={filteredData.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-md font-medium transition-colors flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className="w-5 h-5" />
              Exportar Excel
            </button>
          </div>
        </div>

        {/* Filtros compactos en una sola fila */}
        <div className="bg-white rounded-lg shadow mb-4 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
             
            </h2>
            <button
              onClick={() => setFilters({
                dateFrom: '',
                dateTo: '',
                department: '',
                municipality: '',
                status: '',
                zone: '',
                evento: ''
              })}
              className="text-sm text-[#c8a88c] hover:text-[#b8987c] font-medium"
            >
              Limpiar filtros
            </button>
          </div>

          <div className="grid grid-cols-7 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fecha desde</label>
              <input
                type="date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fecha hasta</label>
              <input
                type="date"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Departamento</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.department}
                onChange={(e) => setFilters({...filters, department: e.target.value, municipality: ''})}
              >
                <option value="">Todos</option>
                {departamentos.map(d => (
                  <option key={d.CodigoDepartamento} value={d.CodigoDepartamento}>
                    {d.NombreDepartamento}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Municipio</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.municipality}
                onChange={(e) => setFilters({...filters, municipality: e.target.value})}
                disabled={!filters.department}
              >
                <option value="">Todos</option>
                {municipios
                  .filter(m => !filters.department || m.CodigoMunicipio?.startsWith(filters.department))
                  .map(m => (
                    <option key={m.CodigoMunicipio} value={m.CodigoMunicipio}>{m.NombreMunicipio}</option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">Todos</option>
                <option value="Por Atender">Por Atender</option>
                <option value="En Atención">En Atención</option>
                <option value="Atendido">Atendido</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Zona Cafetalera</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.zone}
                onChange={(e) => setFilters({...filters, zone: e.target.value})}
              >
                <option value="">Todas</option>
                {uniqueZones.map(z => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Evento/Fenómeno</label>
              <select
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                value={filters.evento}
                onChange={(e) => setFilters({...filters, evento: e.target.value})}
              >
                <option value="">Todos</option>
                {eventos.map(ev => (
                  <option key={ev.Id} value={ev.Id}>{ev.Fenomeno}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabs de navegación */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'general', label: 'General', icon: TrendingUp },
                { id: 'departamentos', label: 'Por Departamento', icon: FileText },
                { id: 'tipos', label: 'Por Tipo', icon: FileText },
                { id: 'regionales', label: 'Por Regional', icon: FileText }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveReport(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeReport === tab.id
                      ? 'border-[#c8a88c] text-[#c8a88c]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contenido de reportes */}
          <div className="p-6">
            {activeReport === 'general' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Total de Emergencias"
                    value={stats.total}
                    subtitle="Registradas en el sistema"
                  />
                  <StatCard
                    title="Atendidas"
                    value={stats.atendidas}
                    subtitle={`${stats.total ? ((stats.atendidas/stats.total)*100).toFixed(1) : 0}% del total`}
                  />
                  <StatCard
                    title="En Atención"
                    value={stats.enAtencion}
                    subtitle={`${stats.total ? ((stats.enAtencion/stats.total)*100).toFixed(1) : 0}% del total`}
                  />
                  <StatCard
                    title="Por Atender"
                    value={stats.porAtender}
                    subtitle={`${stats.total ? ((stats.porAtender/stats.total)*100).toFixed(1) : 0}% del total`}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <StatCard
                    title="Inversión Total"
                    value={`L. ${stats.inversionTotal.toLocaleString()}`}
                    subtitle="Monto ejecutado"
                  />
                  <StatCard
                    title="Inversión Proyectada"
                    value={`L. ${stats.inversionProyectada.toLocaleString()}`}
                    subtitle="Monto estimado"
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Últimas Emergencias Registradas</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Municipio</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Estado</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Inversión</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredData.slice(0, 10).map(e => (
                          <tr key={e.Id} className="hover:bg-white transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatearFecha(e.Fecha)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{e.NombreMunicipio}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{e.NombreTipoEmergencia}</td>
                            <td className="px-4 py-3">
                              <EstadoClickeable emergencia={e} />
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              L. {(e.InversionTotal || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Reporte por Departamentos */}
            {activeReport === 'departamentos' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Emergencias por Departamento</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Departamento</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Atendidas</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">En Atención</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Por Atender</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Inversión Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {departmentStats.map((d, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{d.name}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{d.total}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{d.atendidas}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{d.enAtencion}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{d.porAtender}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                            L. {d.inversion.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Reporte por Tipos */}
            {activeReport === 'tipos' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Emergencias por Tipo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {typeStats.map((t, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{t.name}</h4>
                        <span className="text-2xl font-bold text-gray-900">{t.count}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Inversión: <span className="font-medium text-gray-900">L. {t.inversion.toLocaleString()}</span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-[#c8a88c] h-2 rounded-full transition-all"
                          style={{width: `${(t.count/stats.total)*100}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reporte por Regionales */}
            {activeReport === 'regionales' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Emergencias por Regional</h3>
                {regionalStats.map((regional, idx) => (
                  <div key={idx} className="space-y-3">
                    {/* Título de la Regional - Diseño Ejecutivo */}
                    <div className="bg-white border-l-4 border-gray-900 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xl font-bold text-gray-900">{regional.name}</h4>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">{regional.totalCount}</div>
                          <div className="text-sm text-gray-600">emergencias</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                        <span>Inversión Total:</span>
                        <span className="font-semibold text-gray-900">L. {regional.totalInversion.toLocaleString()}</span>
                      </div>
                    </div>
                    
                    {/* Departamentos de esta regional */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-4">
                      {regional.departamentos.map((dept, deptIdx) => (
                        <div key={deptIdx} className="bg-white rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-semibold text-gray-900 text-sm">{dept.name}</h5>
                            <span className="text-2xl font-bold text-gray-900">{dept.count}</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between text-gray-600">
                              <span>Inversión:</span>
                              <span className="font-medium text-gray-900">L. {dept.inversion.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-gray-600">
                              <span>% del total:</span>
                              <span className="font-medium text-gray-900">{stats.total ? ((dept.count/stats.total)*100).toFixed(1) : 0}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Registrar Ejecución */}
      <ModalRegistrarEjecucion
        isOpen={modalEjecucion}
        onClose={() => setModalEjecucion(false)}
        emergencia={emergenciaSeleccionada}
        onSuccess={handleSuccess}
      />

      {/* Modal de Boletines */}
      <ModalBoletines
        isOpen={modalBoletines}
        onClose={() => setModalBoletines(false)}
        emergencias={filteredData}
      />
    </div>
  );
};

export default Reportes;